import React, { useState, useEffect } from 'react';
import styles from './AdCarousel.module.scss';
import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';

export type CarouselSlide = {
  id: string | number;
  title: string;
  image: string;
};

type AdCarouselProps = {
  slides: CarouselSlide[];
  autoPlay?: boolean;
  autoPlayInterval?: number;
  showArrows?: boolean;
  showDots?: boolean;
  arrowPosition?: 'top' | 'center' | 'bottom';
  dotPosition?: 'top' | 'center' | 'bottom';
  className?: string;
  arrowLeftClassName?: string;
  arrowRightClassName?: string;
  dotsClassName?: string;
  arrowLeftStyle?: React.CSSProperties;
  arrowRightStyle?: React.CSSProperties;
  dotsStyle?: React.CSSProperties;
};

function AdCarousel({
  slides,
  autoPlay = true,
  autoPlayInterval = 5000,
  showArrows = true,
  showDots = true,
  className = '',
  arrowLeftClassName = '',
  arrowRightClassName = '',
  dotsClassName = '',
  arrowLeftStyle,
  arrowRightStyle,
  dotsStyle,
}: AdCarouselProps) {
  const [currentSlide, setCurrentSlide] = useState(0);

  // Auto-play functionality
  useEffect(() => {
    if (!autoPlay || slides.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, autoPlayInterval);

    return () => clearInterval(interval);
  }, [autoPlay, autoPlayInterval, slides.length]);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const goToPrevious = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToNext = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  if (!slides || slides.length === 0) {
    return null;
  }

  // Ensure first slide is always active when there's only one slide
  const activeIndex = slides.length === 1 ? 0 : currentSlide;
  const isSingleSlide = slides.length === 1;
  
  // Calculate previous slide index
  const previousIndex = slides.length > 1 
    ? (currentSlide - 1 + slides.length) % slides.length 
    : -1;

  return (
    <div className={`${styles.carousel} ${className}`}>
      {/* Slides Container */}
      <div className={`${styles.slidesWrapper} ${isSingleSlide ? styles.singleSlide : ''}`}>
        {slides.map((slide, index) => {
          const isActive = index === activeIndex;
          const isPrevious = index === previousIndex && slides.length > 1;
          
          return (
            <div
              key={slide.id}
              style={{ backgroundImage: `url(${slide.image})` }}
              className={`${styles.slide} ${
                isActive ? styles.active : ''
              } ${isPrevious ? styles.previous : ''}`}
            >
              
            </div>
          );
        })}
      </div>

      {/* Navigation Arrows - Outside slider */}
      {showArrows && slides.length > 1 && (
        <>
          <button
            className={`${styles.arrow} ${styles.arrowLeft} ${arrowLeftClassName}`}
            style={arrowLeftStyle}
            onClick={goToPrevious}
            aria-label="Previous slide"
          >
            <ArrowLeftOutlined />
          </button>
          <button
            className={`${styles.arrow} ${styles.arrowRight} ${arrowRightClassName}`}
            style={arrowRightStyle}
            onClick={goToNext}
            aria-label="Next slide"
          >
            <ArrowRightOutlined />
          </button>
        </>
      )}

      {/* Dots Indicator - Outside slider */}
      {showDots && slides.length > 1 && (
        <div 
          className={`${styles.dots} ${dotsClassName}`}
          style={dotsStyle}
        >
          {slides.map((slide, index) => (
            <button
              key={slide.id}
              className={`${styles.dot} ${
                index === currentSlide ? styles.active : ''
              }`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default AdCarousel;

